import { Person } from "@mui/icons-material";
import { getAvatarColorById } from "./avatarColors";
import { resolveAbsoluteUrl } from "./urlUtils";

/**
 * Generate consistent avatar color for a user
 * @param {Object} user - User object with id
 * @returns {string} Hex color code
 */
export const getAvatarColor = (user) => {
  if (!user) return getAvatarColorById(0);

  // استخدام معرف المستخدم للحصول على لون فريد
  const userId = user.id || user.user_id || 0;
  return getAvatarColorById(userId);
};

/**
 * Generate consistent avatar initials for a user
 * @param {Object} user - User object with username, first_name, last_name, email
 * @returns {string|ReactElement} Initials or Person icon
 */
export const getAvatarInitials = (user) => {
  if (!user) return <Person />;

  // Priority 1: first_name + last_name
  if (user.first_name && user.last_name) {
    return `${user.first_name.charAt(0).toUpperCase()}${user.last_name
      .charAt(0)
      .toUpperCase()}`;
  }

  // Priority 2: first_name only
  if (user.first_name) {
    return user.first_name.charAt(0).toUpperCase();
  }

  // Priority 3: last_name only
  if (user.last_name) {
    return user.last_name.charAt(0).toUpperCase();
  }

  // Priority 4: username (first character)
  if (user.username) {
    return user.username.charAt(0).toUpperCase();
  }

  // Priority 5: user_name (first character)
  if (user.user_name) {
    return user.user_name.charAt(0).toUpperCase();
  }

  // Priority 6: email (first character)
  if (user.email) {
    return user.email.charAt(0).toUpperCase();
  }

  return <Person />;
};

/**
 * Get consistent user identifier for avatar generation
 * @param {Object} user - User object
 * @returns {string} Consistent identifier
 */
export const getUserIdentifier = (user) => {
  if (!user) return "";

  // Priority order for consistent identification
  // Priority 1: first_name + last_name
  if (user.first_name && user.last_name) {
    return `${user.first_name} ${user.last_name}`;
  }

  // Priority 2: first_name only
  if (user.first_name) return user.first_name;

  // Priority 3: last_name only
  if (user.last_name) return user.last_name;

  // Priority 4: username
  if (user.username) return user.username;

  // Priority 5: user_name
  if (user.user_name) return user.user_name;

  // Priority 6: email
  if (user.email) return user.email;

  return "";
};

/**
 * Get the best available image source for avatar
 * @param {Object} user - User object
 * @returns {string|null} Image URL or null
 */
export const getAvatarImageSrc = (user) => {
  if (!user) return null;

  // Priority order for image sources
  let imageSrc =
    user.profile_picture ||
    user.avatar ||
    user.profile_image ||
    user.image ||
    null;

  // Convert relative URL to absolute URL if needed
  imageSrc = resolveAbsoluteUrl(imageSrc);

  return imageSrc;
};

/**
 * Check if user has a profile picture
 * @param {Object} user - User object
 * @returns {boolean} True if user has profile picture
 */
export const hasProfilePicture = (user) => {
  return !!getAvatarImageSrc(user);
};

/**
 * Get avatar props for Material-UI Avatar component
 * @param {Object} user - User object
 * @returns {Object} Avatar props
 */
export const getAvatarProps = (user) => {
  const initials = getAvatarInitials(user);
  const color = getAvatarColor(user);
  const imageSrc = getAvatarImageSrc(user);

  return {
    src: imageSrc,
    sx: {
      bgcolor: imageSrc ? undefined : color, // Don't set background color if image exists
    },
    children: imageSrc ? undefined : initials, // Don't show initials if image exists
  };
};
